import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';
import LoadingSpinner from './sms/LoadingSpinner';
import ErrorDisplay from './sms/ErrorDisplay';
import DateFilter from './sms/DateFilter';
import CallsTable from './calls/CallsTable';
import CallModal from './calls/CallModal';

function CallLogs({ cachedConversations, cachedLastKey }) {
  const navigate = useNavigate();
  const { cache } = useDataCache();
  const [allConversations, setAllConversations] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetails, setConversationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Date range filter state - default to last 3 days
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 3);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    const data = cachedConversations || cache.logs?.conversations;
    if (data) {
      setAllConversations(data);
      setLoading(false);
    } else {
      loadConversations();
    }
  }, [cachedConversations, cache.logs?.conversations]);

  useEffect(() => {
    const startTimestamp = new Date(startDate + 'T00:00:00').getTime();
    const endTimestamp = new Date(endDate + 'T23:59:59').getTime();
    
    const filtered = allConversations.filter(conv => {
      const convTime = new Date(conv.start_time).getTime();
      return convTime >= startTimestamp && convTime <= endTimestamp;
    });
    
    setConversations(filtered);
  }, [startDate, endDate, allConversations]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await configService.getConversations(100);
      setAllConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      if (error.message.includes('Authentication required') || error.message.includes('NotAuthorizedException')) {
        navigate('/login');
        return;
      }
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetails = async (conversationId) => {
    try {
      setLoadingDetails(true);
      const details = await configService.getConversationDetails(conversationId);
      setConversationDetails(details);
      setSelectedConversation(conversationId);
    } catch (error) {
      console.error('Failed to load conversation details:', error);
      setError(error.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRowClick = (conversation) => {
    if (!conversation.end_time) {
      navigate(`/live/${conversation.conversation_id}`);
    } else {
      loadConversationDetails(conversation.conversation_id);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown';
    const cleaned = phone.replace(/^\+1/, '').replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Unknown';
    const duration = Math.floor((endTime - startTime) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const closeModal = () => {
    setSelectedConversation(null);
    setConversationDetails(null);
  };

  if (loading) {
    return <LoadingSpinner message="Loading call logs..." />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={loadConversations} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <button
          onClick={loadConversations}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold flex items-center justify-center"
        >
          Refresh
        </button>
      </div>
      
      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={(e) => setStartDate(e.target.value)}
        onEndDateChange={(e) => setEndDate(e.target.value)}
        count={conversations.length}
      />

      <CallsTable
        conversations={conversations}
        onRowClick={handleRowClick}
        formatPhoneNumber={formatPhoneNumber}
        formatDuration={formatDuration}
      />

      <CallModal
        isOpen={!!selectedConversation}
        onClose={closeModal}
        loadingDetails={loadingDetails}
        conversationDetails={conversationDetails}
        formatPhoneNumber={formatPhoneNumber}
        formatDuration={formatDuration}
      />
    </div>
  );
}

export default CallLogs;